/**
 * Map parsed CSV rows → validated vendor create-payloads for the import
 * dialog. Each data row is coerced into a candidate object (header → field),
 * validated against the SAME `vendors.createInput` schema the server uses
 * (so the preview's verdict matches what the API will accept), then tagged:
 *
 *   • `ready`     — passes validation; will be submitted.
 *   • `duplicate` — passes validation but its GSTIN repeats an earlier row
 *                   in the file; skipped client-side to avoid a double-add.
 *   • `error`     — fails validation; the first issue is surfaced to the user.
 *
 * Existing-in-database GSTIN clashes are NOT detected here — the server
 * skips those and reports them back after submit.
 */
import { vendors as vendorsContract } from "@flint/api-contract";
import { parseCsv } from "./parse-csv.ts";

export type VendorCreatePayload = vendorsContract.CreateInput;

export interface ImportPreviewRow {
  /** 1-based line number within the file's data rows (excludes the header). */
  line: number;
  name: string;
  status: "ready" | "duplicate" | "error";
  message?: string;
  payload?: VendorCreatePayload;
}

export interface ImportPreview {
  rows: ImportPreviewRow[];
  readyCount: number;
  duplicateCount: number;
  errorCount: number;
  /** Set when the file has no recognisable header / no `name` column. */
  headerError?: string;
}

/** Normalise a header cell to a lookup key: lowercased, spaces collapsed. */
function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replaceAll(/[\s_]+/g, " ");
}

const HEADER_MAP: Record<string, keyof VendorCreatePayload> = {
  name: "name",
  "vendor name": "name",
  "display name": "displayName",
  gstin: "gstin",
  gst: "gstin",
  contact: "contactName",
  "contact name": "contactName",
  "contact role": "contactRole",
  phone: "phone",
  "phone number": "phone",
  mobile: "phone",
  email: "email",
  "e mail": "email",
  address: "addressLine1",
  "address 1": "addressLine1",
  "address line 1": "addressLine1",
  "address 2": "addressLine2",
  "address line 2": "addressLine2",
  city: "city",
  state: "state",
  pin: "pincode",
  pincode: "pincode",
  "pin code": "pincode",
  terms: "paymentTerms",
  "payment terms": "paymentTerms",
  tds: "tdsSection",
  "tds section": "tdsSection",
  "job worker": "isJobWorker",
  "is job worker": "isJobWorker",
  msme: "msmeRegistered",
  "msme registered": "msmeRegistered",
  category: "vendorCategory",
  "vendor category": "vendorCategory",
  tags: "tags",
};

const BOOLEAN_FIELDS = new Set<keyof VendorCreatePayload>(["isJobWorker", "msmeRegistered"]);
const ARRAY_FIELDS = new Set<keyof VendorCreatePayload>(["tags"]);

/** The headers the downloadable template ships with (order matters). */
export const TEMPLATE_HEADERS = [
  "Name",
  "GSTIN",
  "Contact",
  "Phone",
  "Email",
  "Address 1",
  "City",
  "State",
  "PIN",
  "Payment terms",
  "TDS section",
  "Job worker",
];

const TEMPLATE_EXAMPLE = [
  "Acme Textiles Pvt Ltd",
  "29ABCDE1234F1Z5",
  "Ramesh Kumar",
  "9845012345",
  "accounts@acme.example",
  "12 MG Road",
  "Bengaluru",
  "Karnataka",
  "560001",
  "Net 30",
  "194C",
  "No",
];

/** A ready-to-fill CSV template string (headers + one worked example row). */
export function importTemplateCsv(): string {
  return `${TEMPLATE_HEADERS.join(",")}\n${TEMPLATE_EXAMPLE.join(",")}\n`;
}

function parseBool(value: string): boolean | undefined {
  if (/^(y|yes|true|1)$/i.test(value)) return true;
  if (/^(n|no|false|0)$/i.test(value)) return false;
  return undefined;
}

export function buildImportPreview(csvText: string): ImportPreview {
  const parsed = parseCsv(csvText);
  if (parsed.length === 0) {
    return { rows: [], readyCount: 0, duplicateCount: 0, errorCount: 0, headerError: "The file is empty." };
  }

  const headerCells = parsed[0]!.map(normalizeHeader);
  const fields = headerCells.map((h) => HEADER_MAP[h]);
  if (!fields.includes("name")) {
    return {
      rows: [],
      readyCount: 0,
      duplicateCount: 0,
      errorCount: 0,
      headerError:
        'No "Name" column found. The first row must be a header that includes at least a Name column.',
    };
  }

  const rows: ImportPreviewRow[] = [];
  const seenGstins = new Set<string>();
  let readyCount = 0;
  let duplicateCount = 0;
  let errorCount = 0;

  for (let r = 1; r < parsed.length; r++) {
    const cells = parsed[r]!;
    const line = r; // 1-based data line

    // Build the candidate object from mapped, non-empty cells.
    const candidate: Record<string, unknown> = {};
    fields.forEach((field, c) => {
      if (!field) return;
      const raw = (cells[c] ?? "").trim();
      if (!raw) return;
      if (BOOLEAN_FIELDS.has(field)) {
        const b = parseBool(raw);
        if (b !== undefined) candidate[field] = b;
      } else if (ARRAY_FIELDS.has(field)) {
        const tags = raw
          .split(/[;,]/)
          .map((t) => t.trim())
          .filter(Boolean);
        if (tags.length) candidate[field] = tags;
      } else {
        candidate[field] = raw;
      }
    });

    const displayName = typeof candidate.name === "string" ? (candidate.name as string) : `Row ${line}`;

    const parsed2 = vendorsContract.createInput.safeParse(candidate);
    if (!parsed2.success) {
      errorCount++;
      const issue = parsed2.error.issues[0];
      const field = issue?.path.join(".");
      const message = field ? `${field}: ${issue?.message ?? "invalid"}` : issue?.message;
      rows.push({
        line,
        name: displayName,
        status: "error",
        ...(message ? { message } : {}),
      });
      continue;
    }

    const payload = parsed2.data;
    if (payload.gstin) {
      const key = payload.gstin.toUpperCase();
      if (seenGstins.has(key)) {
        duplicateCount++;
        rows.push({
          line,
          name: payload.name,
          status: "duplicate",
          message: `GSTIN ${payload.gstin} repeats an earlier row.`,
        });
        continue;
      }
      seenGstins.add(key);
    }

    readyCount++;
    rows.push({ line, name: payload.name, status: "ready", payload });
  }

  return { rows, readyCount, duplicateCount, errorCount };
}
